import * as XLSX from 'xlsx';
import {Moment} from 'moment';
import moment from 'moment';
import {TFunction} from 'i18next';
import {EntityInfo} from '../../../../backend/interfaces/entity-info';
import {EntityShift} from '../../../../backend/interfaces/entity-shift';
import {EntityShiftsAllocationsTableRow} from '../../../../backend/interfaces/entity-shifts-allocations-table';
import {translations} from '../../../../i18n/translations';
import type {PreparedXlsxExportFile} from '../base/export-data-types';
import {buildExportFileName} from '../base/export-file-name';

type PrepareShiftsAllocationsXLSXParams = {
  entity?: EntityInfo;
  rows: EntityShiftsAllocationsTableRow[];
  shifts: EntityShift[];
  startDate: Moment;
  endDate: Moment;
  t: TFunction;
};

export function prepareShiftsAllocationsXLSX({
  entity,
  rows,
  shifts,
  startDate,
  endDate,
  t,
}: PrepareShiftsAllocationsXLSXParams): PreparedXlsxExportFile {
  const selectedWeekDays = Array.from(Array(endDate.diff(startDate, 'days')), (_value, index) =>
    startDate.clone().add(index, 'days')
  );

  const shiftById = new Map(shifts.map((shift) => [shift.id ?? '', shift]));
  const headerRow = [
    t(translations.office.professional_caregiver.shifts_allocations.table.collaborators),
    ...selectedWeekDays.map((date) => {
      const label = date.format('dddd');

      return `${date.format('DD/MM/YYYY')} (${label})`;
    }),
  ];

  const bodyRows = rows.map((row) => {
    return [
      row.user.name,
      ...selectedWeekDays.map((date) => {
        const shiftLabels = row.allocations
          .filter((allocation) => moment(allocation.day).isSame(date, 'day'))
          .map((allocation) => {
            if (!allocation.entityShiftId) {
              return '';
            }

            const shift = shiftById.get(allocation.entityShiftId);
            return shift ? formatShiftLabel(shift) : '';
          })
          .filter(Boolean);

        return shiftLabels.join(', ');
      }),
    ];
  });

  const worksheet = XLSX.utils.aoa_to_sheet([headerRow, ...bodyRows]);
  worksheet['!cols'] = [{wch: 24}, ...selectedWeekDays.map(() => ({wch: 28}))];

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(
    workbook,
    worksheet,
    t(translations.teamSideMenu.shifts.shiftsAllocations)
  );

  const fileName = buildExportFileName([
    entity?.shortName ?? entity?.name,
    t(translations.teamSideMenu.shifts.shiftsAllocations),
  ]);

  return {
    file: workbook,
    fileName,
  };
}

function formatShiftLabel(shift: EntityShift): string {
  if (shift.abbreviation && shift.name) {
    return `${shift.abbreviation} (${shift.name})`;
  }

  return shift.abbreviation || shift.name || '';
}
